/**
 * Template + saved creative element merge and layout helpers for the creative editor canvas.
 */

import type { CSSProperties } from 'react';

import type { Asset, Template } from '@/types/api';

export interface AssetFileShape {
  uri: string;
  name: string;
  asset_type: string;
  organization_id: number;
}

export function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
}

export function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

export function deepMergeElement(
  base: Record<string, unknown>,
  cur: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base, ...cur };
  const bd = asRecord(base.element_data);
  const cd = asRecord(cur.element_data);
  if (bd && cd) {
    const mergedData: Record<string, unknown> = { ...bd };
    for (const k of Object.keys(cd)) {
      const bdk = mergedData[k];
      const cdk = cd[k];
      if (
        bdk &&
        typeof bdk === 'object' &&
        !Array.isArray(bdk) &&
        cdk &&
        typeof cdk === 'object' &&
        !Array.isArray(cdk)
      ) {
        mergedData[k] = { ...(bdk as object), ...(cdk as object) };
      } else if (cdk !== undefined) {
        mergedData[k] = cdk;
      }
    }
    out.element_data = mergedData;
  }
  return out;
}

export function buildWorkingElements(template: Template, saved: unknown[]): unknown[] {
  const t = template.elements;
  const s = Array.isArray(saved) ? saved : [];
  return t.map((def, i) => {
    const base = deepClone(def) as Record<string, unknown>;
    const cur = asRecord(s[i]);
    if (!cur) return base;
    return deepMergeElement(base, cur);
  });
}

export function sortedIndices(template: Template): number[] {
  return template.elements
    .map((_, i) => i)
    .sort((a, b) => {
      const ea = asRecord(template.elements[a]);
      const eb = asRecord(template.elements[b]);
      const za = Number(ea?.z_index ?? 0);
      const zb = Number(eb?.z_index ?? 0);
      if (za !== zb) return za - zb;
      const oa = Number(ea?.order_rank ?? 0);
      const ob = Number(eb?.order_rank ?? 0);
      return oa - ob;
    });
}

/** Canonical 16:9 design canvas; matches `DESIGN_CANVAS_*_PX` in `format_elements.py`. */
export const DESIGN_CANVAS_WIDTH_PX = 1280;
export const DESIGN_CANVAS_HEIGHT_PX = 720;

export function isRectanglePositioning(
  p: unknown,
): p is { x_pos: number; y_pos: number; width: number; height: number } {
  const r = asRecord(p);
  if (!r) return false;
  return (
    typeof r.x_pos === 'number' &&
    typeof r.y_pos === 'number' &&
    typeof r.width === 'number' &&
    typeof r.height === 'number'
  );
}

export function isCenteredPositioning(
  p: unknown,
): p is { width: number; height: number; centered?: boolean; fit_to_canvas?: boolean } {
  const r = asRecord(p);
  if (!r) return false;
  if (typeof r.x_pos === 'number') return false;
  return typeof r.width === 'number' && typeof r.height === 'number';
}

export function layoutStyleFromElementDef(
  def: Record<string, unknown>,
  canvasWidthPx: number = DESIGN_CANVAS_WIDTH_PX,
  canvasHeightPx: number = DESIGN_CANVAS_HEIGHT_PX,
): CSSProperties {
  const p = def.positioning;
  const CW =
    Number.isFinite(canvasWidthPx) && canvasWidthPx > 0 ? canvasWidthPx : DESIGN_CANVAS_WIDTH_PX;
  const CH =
    Number.isFinite(canvasHeightPx) && canvasHeightPx > 0
      ? canvasHeightPx
      : DESIGN_CANVAS_HEIGHT_PX;
  if (isRectanglePositioning(p)) {
    return {
      left: `${(p.x_pos / CW) * 100}%`,
      top: `${(p.y_pos / CH) * 100}%`,
      width: `${(p.width / CW) * 100}%`,
      height: `${(p.height / CH) * 100}%`,
    };
  }
  if (isCenteredPositioning(p)) {
    const w = p.width;
    const h = p.height;
    return {
      left: `${((CW - w) / 2 / CW) * 100}%`,
      top: `${((CH - h) / 2 / CH) * 100}%`,
      width: `${(w / CW) * 100}%`,
      height: `${(h / CH) * 100}%`,
    };
  }
  return { left: 0, top: 0, width: '100%', height: '100%' };
}

export function stackingZIndex(def: Record<string, unknown>): number {
  return Number(def.z_index ?? 0) * 100 + Number(def.order_rank ?? 0);
}

export function findAssetForFile(
  assetFile: AssetFileShape,
  assetsList: Asset[],
): Asset | undefined {
  return assetsList.find(
    (a) =>
      a.uri === assetFile.uri ||
      (a.name === assetFile.name && a.organization_id === assetFile.organization_id),
  );
}

export function imagePreviewSrc(
  row: Record<string, unknown> | null,
  def: Record<string, unknown> | null,
): string | undefined {
  const data = asRecord(row?.element_data) ?? {};
  const af = asRecord(asRecord(data.image_element)?.asset_file);
  const uri = af?.uri as string | undefined;
  if (uri) return uri;
  const ph = row?.placeholder_asset
    ? String(row.placeholder_asset)
    : def?.placeholder_asset
      ? String(def.placeholder_asset)
      : undefined;
  return ph;
}
